<script setup lang="ts">
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { format as formatDate } from "date-fns";
import { Loader2, Trash2 } from "@lucide/vue";

import { useModalStore } from "@/stores/modals/modals.store";
import { useProjectRevisionsStore } from "../stores/projectRevisions.store";
import type { ProjectRevision } from "../types/projectRevision.types";

// Confirmation for deleting a save point.
//
// The dialog deliberately does not say "this cannot be undone", because it can: deleting a revision
// hides it, and its rows, its child rows and its Marten pin all stay where they are. What it costs
// is the way back to this state through the app, which is the thing worth confirming. The revision
// the project is currently ON never reaches this dialog — the caller hides the action and the
// server refuses it — so there is no warning here about losing the working state.
const props = defineProps<{
  projectId: string;
  revision: ProjectRevision;
}>();

const toast = useToast();
const { closeByName } = useModalStore();
const revisionsStore = useProjectRevisionsStore();

const isDeleting = ref(false);

const savedAt = computed(() => formatDate(new Date(props.revision.createdAt), "MMM d, yyyy 'at' HH:mm"));

const cancel = () => closeByName("DeleteProjectRevisionModal", { action: "cancel" });

const confirm = async () => {
  if (isDeleting.value) return;
  isDeleting.value = true;
  try {
    await revisionsStore.deleteRevision(props.projectId, props.revision.id, props.revision.name);
    toast.success(`Revision “${props.revision.name}” deleted.`);
    closeByName("DeleteProjectRevisionModal", { action: "deleted" });
  } catch (e) {
    Sentry.captureException(e);
    toast.error("Could not delete the revision. Please try again.");
  } finally {
    isDeleting.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Delete “{{ revision.name }}”?</h2>

    <p class="t-small text-type-muted">
      This save point disappears from the revision list and the project toolbar, and nobody on the project can preview it or
      continue from it any more.
    </p>

    <!-- What is being deleted -->
    <div class="rounded-8 p-px-12 gap-px-4 flex flex-col bg-slate-100 ring-1 ring-slate-200">
      <p class="t-small-bold text-type-default">{{ revision.name }}</p>
      <p class="t-caption text-type-muted">{{ savedAt }} · {{ revision.createdBy.name }}</p>
    </div>

    <p class="t-caption text-type-muted">
      The project you are working on is not affected — the current revision, its materials and its mappings all stay as they are.
    </p>

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isDeleting"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isDeleting },
        ]"
        data-cy="delete-revision-cancel"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="isDeleting"
        :class="[
          btnBase,
          !isDeleting
            ? 'bg-app-red-600 dark:bg-app-red-fill hover:bg-app-red-700 dark:hover:bg-app-red-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': isDeleting },
        ]"
        data-cy="delete-revision-confirm"
        @click="confirm">
        <Loader2
          v-if="isDeleting"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        <Trash2
          v-else
          class="icon-sm"
          :stroke-width="1.5" />
        {{ isDeleting ? "Deleting…" : "Delete revision" }}
      </button>
    </div>
  </div>
</template>
